/**
 * Post Index Component
 *
 * Light DOM Web Component that highlights the current section in the TOC.
 * Uses IntersectionObserver for performant scroll tracking.
 * Progressive enhancement - anchor links work without JS.
 *
 * Usage: <post-index>...server-rendered markup...</post-index>
 *
 * @package Felix
 * @since 1.0.0
 */

class PostIndex extends HTMLElement {
	connectedCallback() {
		this.links = this.querySelectorAll('.post-index__link')
		this.toggle = this.querySelector('.post-index__toggle')
		this.nav = this.querySelector('nav')
		this.headings = []

		// Header offset for scroll (adjust based on sticky header height)
		this.scrollOffset = 100

		// Get all target headings from links
		this.links.forEach((link) => {
			const href = link.getAttribute('href')
			if (!href || !href.startsWith('#')) return

			const id = href.slice(1)
			const heading = document.getElementById(id)

			if (heading) {
				this.headings.push({ id, heading, link })
			}
		})

		if (this.headings.length === 0) return

		this.toggle.addEventListener('click', () => {
			this.nav.classList.toggle('post-index__nav--active')
		})

		// Set first link as active by default
		this.setActive(this.headings[0].id)

		// Add smooth scroll with offset
		this.initSmoothScroll()

		// Initialize scroll tracking
		this.initObserver()
	}

	toggleNav() {
		this.nav.classList.toggle('post-index__nav--active')
	}

	initSmoothScroll() {
		this.links.forEach((link) => {
			link.addEventListener('click', (e) => {
				e.preventDefault()

				const href = link.getAttribute('href')
				if (!href || !href.startsWith('#')) return

				const id = href.slice(1)
				const heading = document.getElementById(id)

				if (heading) {
					const targetPosition = heading.getBoundingClientRect().top + window.scrollY - this.scrollOffset

					window.scrollTo({
						top: targetPosition,
						behavior: 'smooth',
					})

					// Update URL hash without jumping
					history.pushState(null, '', href)
				}
			})
		})
	}

	initObserver() {
		// rootMargin: account for header offset
		// Top margin pushes trigger point down, bottom margin limits active zone
		const options = {
			rootMargin: `-${this.scrollOffset}px 0px -70% 0px`,
			threshold: 0,
		}

		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.setActive(entry.target.id)
				}
			})
		}, options)

		this.headings.forEach(({ heading }) => {
			this.observer.observe(heading)
		})
	}

	setActive(activeId) {
		this.links.forEach((link) => {
			const isActive = link.getAttribute('href') === `#${activeId}`
			link.classList.toggle('post-index__link--active', isActive)
		})
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect()
		}
	}
}

customElements.define('post-index', PostIndex)
